import { Paper } from "@mantine/core";
import { productApi } from "apis/productApi";
import { Loading } from "components";
import { ErrorStatusType } from "constants/types/common";
import {
  ProductRequestBodyType,
  ProductResponseType,
} from "constants/types/product";
import { useFormik } from "formik";
import { FORM_IDS } from "pages/Product/config";
import { uploadProductImage } from "pages/Product/config";
import { FC, useRef, useState } from "react";
import { useNotification } from "utils/hooks";
import { useListMeasurementUnit } from "utils/hooks/request/useMeasurementUnits";
import { useListProductCategory } from "utils/hooks/request/useProductCategories";
import ProductFormFields from "../ProductFormFields/ProductFormFields";
import { editValidationSchema } from "./config";

interface EditFormProps {
  product: ProductResponseType;
  onClose?: () => void;
  mutate?: () => void;
}

const EditForm: FC<EditFormProps> = ({ product, onClose, mutate }) => {
  const { notification } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // null = xoá ảnh; undefined = chưa đổi (giữ nguyên product.imageKey)
  const [selectedImageFile, setSelectedImageFile] = useState<File | null | undefined>(undefined);
  // Giữ key sau upload thành công để tránh upload lại khi retry
  const uploadedImageKeyRef = useRef<string | null>(null);

  const {
    measurementOptions,
    isLoading: isMeasurementLoading,
    mutate: measurementMutate,
  } = useListMeasurementUnit();

  const {
    productCategoryOptions,
    isLoading: isCategoryLoading,
    mutate: categoryMutate,
  } = useListProductCategory();

  const handleImageFileChange = (file: File | null) => {
    setSelectedImageFile(file);
    // Nếu xoá ảnh hoặc chọn file mới, reset key đã upload
    if (!file) uploadedImageKeyRef.current = null;
    else if (file !== selectedImageFile) uploadedImageKeyRef.current = null;
  };

  const formik = useFormik<ProductRequestBodyType>({
    initialValues: {
      code: product.code,
      name: product.name,
      spec: product.spec ?? "",
      unitId: product.unitId,
      priceNet: product.priceNet,
      categoryId: product.categoryId,
      isActive: product.isActive,
      notes: product.notes ?? "",
    },
    validationSchema: editValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        let imageKey: string | null | undefined;

        if (selectedImageFile instanceof File) {
          // Chọn file mới: upload nếu chưa có key
          if (!uploadedImageKeyRef.current) {
            try {
              uploadedImageKeyRef.current = await uploadProductImage(selectedImageFile);
            } catch {
              notification.error({ title: "Upload ảnh thất bại, vui lòng thử lại" });
              setIsSubmitting(false);
              return;
            }
          }
          imageKey = uploadedImageKeyRef.current;
        } else if (selectedImageFile === null) {
          // Xoá ảnh
          imageKey = null;
        } else {
          // Không đổi ảnh: giữ nguyên key hiện có
          imageKey = product.imageKey;
        }

        const res = await productApi.update(product.id, { ...values, imageKey });
        if (res.data.success) {
          notification.success({ title: "Cập nhật mặt hàng thành công" });
          onClose?.();
          mutate?.();
        }
      } catch (error: ErrorStatusType | any) {
        notification.error({
          title: "Cập nhật mặt hàng thất bại",
          message: error?.error?.message,
        });
      }
      setIsSubmitting(false);
    },
  });

  const isLoading = isSubmitting || isMeasurementLoading || isCategoryLoading;

  // Hiển thị ảnh: nếu user đang xoá thì không truyền currentImageKey
  const currentImageKey = selectedImageFile === null ? null : product.imageKey;

  return (
    <Loading variant="overlay" visible={isLoading}>
      <Paper>
        <form id={FORM_IDS.EDIT} onSubmit={formik.handleSubmit}>
          <ProductFormFields
            formik={formik}
            measurementOptions={measurementOptions ?? []}
            productCategoryOptions={productCategoryOptions ?? []}
            onMeasurementMutate={measurementMutate}
            onCategoryMutate={categoryMutate}
            currentImageKey={currentImageKey}
            selectedImageFile={selectedImageFile instanceof File ? selectedImageFile : null}
            onImageFileChange={handleImageFileChange}
          />
        </form>
      </Paper>
    </Loading>
  );
};

export default EditForm;
